import type { ReactNode } from 'react'
import { Icon, Sparkline, cx, type Tone } from '@w7/ui'
import { EMPTY, lll, money, toneOf } from '@/lib/format'
import { useViewerCurrency } from '@/api/hooks/shared/useViewerCurrency'
import type { CurrencyType } from '@/features/hierarchy/hierarchy'

export function Money({
  value,
  signed = false,
  compact = false,
  tone: forcedTone,
  invert = false,
  className,
  currency,
  symbol = true,
}: {
  value: number | null | undefined
  signed?: boolean
  compact?: boolean
  tone?: Tone
  invert?: boolean
  className?: string
  currency?: string
  /** Show ₹ / Pts. Default true. Lab My P/L uses bare indianCurrency figures. */
  symbol?: boolean
}) {
  const viewer = useViewerCurrency()
  const ccy = (currency ?? viewer) as CurrencyType
  const tone = forcedTone ?? toneOf(value, invert)
  const formatted = money(value, { signed, currency: ccy, round: compact, symbol })
  return (
    <span
      className={cx('w7-mono', `w7-tone-${tone}`, className)}
      title={compact ? formatted : undefined}
    >
      {formatted}
    </span>
  )
}

export function Amount({
  value,
  compact = false,
  currency,
}: {
  value: number | null | undefined
  compact?: boolean
  currency?: string
}) {
  return <Money value={value} tone="neutral" compact={compact} currency={currency} />
}

export function AsOf({ at }: { at?: string | number | null }) {
  return (
    <span className="w7-asof">
      <Icon name="clock" size={12} />
      As of {at ? lll(at) : EMPTY}
    </span>
  )
}

export function exportCsv(filename: string, rows: Record<string, unknown>[]) {
  if (rows.length === 0) return
  const headers = Object.keys(rows[0]!)
  const escape = (value: unknown) => {
    const text = value === null || value === undefined ? '' : String(value)
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
  }
  const csv = [headers.join(','), ...rows.map((row) => headers.map((h) => escape(row[h])).join(','))].join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

export function MiniSpark({ values, invert = false }: { values: number[]; invert?: boolean }) {
  const first = values[0] ?? 0
  const last = values[values.length - 1] ?? 0
  const direction = invert ? first - last : last - first
  return (
    <Sparkline
      values={values}
      width={64}
      height={22}
      color={
        direction > 0 ? 'var(--w7-profit)' : direction < 0 ? 'var(--w7-loss)' : 'var(--w7-text-3)'
      }
    />
  )
}

export function SectionGrid({
  columns = 2,
  children,
}: {
  columns?: 1 | 2 | 3 | 4
  children: ReactNode
}) {
  return <div className={`w7-grid w7-grid-${columns}`}>{children}</div>
}
